import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'react-fast-compare';
import { Form } from 'react-bootstrap';
import { Field, useFormikContext } from 'formik';
import { faHandPointRight } from '@fortawesome/pro-regular-svg-icons/faHandPointRight';

import Placeholder from 'components/Placeholder';
import { duckify, filterDuck } from 'utils/duckify';
import {
  reviewChecklistItems as reviewChecklistItemsDuck,
  reviewActivityItems as reviewActivityItemsDuck,
  reviewConfigs as reviewConfigsDuck
} from 'ducks';
import FA from 'utils/FA';
import ReviewPage from './ReviewPage';
import './styles/DocumentsForReview.scss';

const Body = () => {
  const {
    values,
    isSubmitting
  } = useFormikContext();
  return (
    <React.Fragment>
      <div className='review-sub-section'>
        <div className='review-sub-section-header'>Checklist Items</div>
        <div className='review-section-help-text'>
          Select which documents from the Inquiry/Admissions Checklists to include for review by your readers.
        </div>
        <div className='review-note'>
          <div className='review-note-header'>
            <FA icon={faHandPointRight} nbsp />
            Note:
          </div>
          Checklist rules will be applied so that an item is&nbsp;
          <strong>only displayed on contacts for whom it is relevant.</strong>
        </div>
        <div className='review-include-message'>
          Include the following documents for review:
        </div>
        <div className='review-checklist-items' name='checklistItemsForm'>
          {values.checklistItems.map((item, index) => (
            <Form.Group key={item.checklist_item_id}>
              <Field
                as={Form.Switch}
                className='review-settings-switch'
                name={`checklistItems.${index}.enabled`}
                label={item.name}
                checked={item.enabled}
                disabled={isSubmitting}
              />
            </Form.Group>
          ))}
        </div>
      </div>
      <div className='review-sub-section'>
        <div className='review-sub-section-header'>Test Scores</div>
        <div className='review-section-help-text'>
          Choose whether to display test scores on a separate page for your readers.
          At this time, only SSAT scores imported through the integration are shown.
        </div>
        <div className='review-include-message'>
          Show a separate page for Test Scores in Reader View:
        </div>
        <div className='review-config-items' name='configForm'>
          {values.configItems.map((item, index) => (
            <Form.Group key={item.id}>
              <Field
                as={Form.Switch}
                className='review-settings-switch'
                name={`configItems.${index}.test_scores_enabled`}
                label='Display Test Scores'
                checked={item.test_scores_enabled}
                disabled={isSubmitting}
              />
            </Form.Group>
          ))}
        </div>
      </div>
      <div className='review-sub-section'>
        <div className='review-sub-section-header'>Notes & Activities</div>
        <div className='review-section-help-text'>
          Select whether to include an “Additional Activity” page for review by your readers.
        </div>
        <div className='review-include-message'>
          Include the following Activity types in Reader View:
        </div>
        <div className='review-activity-items' name='activityItemsForm'>
          {values.activityItems.map((item, index) => (
            <Form.Group key={item.activity_item_id}>
              <Field
                as={Form.Switch}
                className='review-settings-switch'
                name={`activityItems.${index}.enabled`}
                label={item.name}
                checked={item.enabled}
                disabled={isSubmitting}
              />
            </Form.Group>
          ))}
        </div>
      </div>
    </React.Fragment>
  );
};

const DocumentsForReview = ({
  className,
  checklistItems,
  activityItems,
  configItems,
  allDucksReady,
  route,
  schoolYearId,
  saveChecklistItems,
  saveActivityItems,
  saveConfigItems,
  reloadDucks
}) => {
  const [formikValues, setFormikValues] = useState({
    checklistItems,
    activityItems,
    configItems: configItems.map(item => ({
      ...item,
      test_scores_enabled: item.test_scores_enabled || false
    }))
  });

  /** Initialize Formik Values */
  useEffect(() => {
    setFormikValues({
      checklistItems,
      activityItems,
      configItems: configItems.map(item => ({
        ...item,
        test_scores_enabled: item.test_scores_enabled || false
      }))
    });
  }, [checklistItems, activityItems, configItems]);

  const headerHelperText = `
    Designate which documents and activities will appear in the Review tab of each contact record.
  `;

  const onSubmitData = {
    checklistItems: {
      fn: saveChecklistItems,
      idName: 'checklist_item_id'
    },
    configItems: {
      fn: saveConfigItems,
      idName: 'id'
    },
    activityItems: {
      fn: saveActivityItems,
      idName: 'activity_item_id'
    }
  };

  /** onSubmit takes a variable number of sections to be submitted,
   *  submits them all and formats the data into one object and then
   *  saves them in state to be passed to ReviewPage as formikValues
   */
  const onSubmit = ({ values, initialFormikValues }) => (
    Promise.all(Object.keys(initialFormikValues)
      .filter(key => !isEqual(initialFormikValues[key], values[key]))
      .map(key => onSubmitData[key].fn({
        items: values[key].map(item => ({
          enabled: item.enabled || Boolean(item.test_scores_enabled),
          [onSubmitData[key].idName]: item[onSubmitData[key].idName]
        })),
        id: schoolYearId
      })
        .then(res => Promise.resolve({
          [key]: res
        }))))
      .then(res => {
        const responseValues = res.reduce((prev, curr) => (
          Object.assign(prev, { [Object.keys(curr)[0]]: curr[Object.keys(curr)[0]] })
        ));
        return Promise.resolve({ ...values, ...responseValues });
      })
      .then(res => {
        setFormikValues(res);
        reloadDucks();
      })
  );

  return (
    <ReviewPage // TODO Implement review settings document controller stuff
      className={className}
      header='Documents for Review'
      headerHelperText={headerHelperText}
      initialFormikValues={formikValues}
      route={route}
      onSubmit={data => onSubmit(data)}
      reloadDucks={reloadDucks}
    >
      <Placeholder.loading
        ready={allDucksReady}
        content={() => <Body />}
      />
    </ReviewPage>
  );
};

DocumentsForReview.propTypes = {
  /** Classname prop */
  className: PropTypes.string,

  /** Current Track Silo Selected School Id */
  schoolYearId: PropTypes.string.isRequired,

  /** Checklist Items Duck */
  checklistItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    checklist_item_id: PropTypes.string,
    name: PropTypes.string,
    enabled: PropTypes.bool
  })).isRequired,

  /** Activity Items Duck */
  activityItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    activity_item_id: PropTypes.string,
    name: PropTypes.string,
    enabled: PropTypes.bool
  })).isRequired,

  /** Configs Duck */
  configItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      customer_id: PropTypes.string,
      school_year_id: PropTypes.string,
      test_scores_enabled: PropTypes.bool
    })
  ).isRequired,

  /** allDucksReady prop */
  allDucksReady: PropTypes.bool.isRequired,

  /** React router route */
  route: PropTypes.shape({}).isRequired,

  /** saveChecklistItems Duck. Dispatches Update */
  saveChecklistItems: PropTypes.func.isRequired,

  /** saveActivityItems Duck. Dispatches Update */
  saveActivityItems: PropTypes.func.isRequired,

  /** saveConfigItems Duck. Dispatches Update */
  saveConfigItems: PropTypes.func.isRequired,

  /** marks ducks dirty for reload */
  reloadDucks: PropTypes.func.isRequired
};

DocumentsForReview.defaultProps = {
  className: ''
};

const mapDucksToProps = props => {
  const { schoolYearId } = props;
  return {
    checklistItems: filterDuck(reviewChecklistItemsDuck, { school_year_id: schoolYearId }),
    activityItems: filterDuck(reviewActivityItemsDuck, { school_year_id: schoolYearId }),
    configItems: filterDuck(reviewConfigsDuck, { school_year_id: schoolYearId })
  };
};

const mapDispatchToProps = (dispatch) => ({
  saveChecklistItems: data => dispatch(reviewChecklistItemsDuck.actions.bulkUpdate(
    data.items,
    { school_year_id: data.id }
  ))
    .then(reviewChecklistItemsDuck.actions.denormalizeObjects),
  saveActivityItems: data => dispatch(reviewActivityItemsDuck.actions.bulkUpdate(
    data.items,
    { school_year_id: data.id }
  ))
    .then(reviewActivityItemsDuck.actions.denormalizeObjects),
  saveConfigItems: data => dispatch(reviewConfigsDuck.actions.bulkUpdate(
    data.items,
    { school_year_id: data.id }
  ))
    .then(reviewConfigsDuck.actions.denormalizeObjects),
  reloadDucks: () => {
    dispatch(reviewChecklistItemsDuck.actions.markDirty());
    dispatch(reviewActivityItemsDuck.actions.markDirty());
    dispatch(reviewConfigsDuck.actions.markDirty());
  }
});

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(DocumentsForReview);
